import { useEffect, useRef } from 'react';
import {
  Input,
  Button,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  Search20Regular,
  Dismiss20Regular,
  ArrowLeft20Regular,
} from '@fluentui/react-icons';
import { useUiStore, useSearchQuery } from '@/stores/uiStore';
import { useTreeStore } from '@/stores/treeStore';
import { resolveScope, contextualScope } from '@/hooks/useSearch';
import { addBreadcrumb } from '@/services/breadcrumbs/breadcrumbs';
import { SearchFiltersPopover } from '@/features/search/SearchFiltersPopover';
import { UserMenu } from './UserMenu';

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalS,
    // SYMÉTRIQUE du padding gauche, et non zéro : l'avatar est le dernier enfant
    // du bandeau, donc un padding droit nul le collait au bord du volet — la
    // seule chose de toute l'interface à toucher le cadre.
    paddingRight: tokens.spacingHorizontalXS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalXS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  searchWrap: { flex: 1, minWidth: 0, display: 'flex' },
  search: { flexGrow: 1, minWidth: 0, maxWidth: '100%' },
  titleWrap: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  title: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase400 },
  after: { display: 'flex', alignItems: 'center', gap: '2px' },
  iconBtn: { minWidth: '24px', width: '24px', height: '24px', padding: 0 },
});

export function SearchHeader() {
  const styles = useStyles();
  const view = useUiStore((s) => s.view);
  const setView = useUiStore((s) => s.setView);
  const searchQuery = useSearchQuery();
  const setSearchQuery = useUiStore((s) => s.setSearchQuery);
  const scopeOverride = useUiStore((s) => s.scopeOverride);
  const setScopeOverride = useUiStore((s) => s.setScopeOverride);

  // "Le dossier affiché" suit la page détail en mode 'detail', sinon le dossier
  // sélectionné dans l'arbre. Une portée dont l'identifiant est indisponible est
  // désactivée dans le menu et retombe sur "Partout" pour l'affichage.
  const mode = useTreeStore((s) => s.mode);
  const activeContainerId = useTreeStore((s) => s.activeContainerId);
  const activeFolderId = useTreeStore((s) => s.activeFolderId);
  const detailFolderId = useTreeStore((s) => s.detailFolderId);
  const visibleFolderId = mode === 'detail' ? detailFolderId : activeFolderId;

  const canContainer = Boolean(activeContainerId);
  const canFolder = Boolean(visibleFolderId);
  // Genre affiché/appliqué = choix explicite s'il existe, sinon défaut contextuel
  // (identique à ce qu'utilisera la requête via useResolvedScope).
  const scopeKind = scopeOverride ?? contextualScope(mode, detailFolderId, activeContainerId);
  const effectiveScope = resolveScope(scopeKind, activeContainerId, visibleFolderId).scope;

  // Changer de contexte de navigation (accueil ↔ projet ↔ dossier, ou d'onglet)
  // oublie le choix manuel pour revenir au défaut contextuel. La signature ne
  // dépend que de ce qui détermine ce défaut, donc re-sélectionner la même portée
  // ou naviguer dans l'arbre (mode 'tree') ne déclenche pas de réinitialisation.
  const navKey =
    mode === 'detail' ? `detail:${activeContainerId ?? ''}:${detailFolderId ?? ''}` : 'tree';
  const prevNavKey = useRef(navKey);
  useEffect(() => {
    if (prevNavKey.current === navKey) return;
    prevNavKey.current = navKey;
    setScopeOverride(null);
  }, [navKey, setScopeOverride]);

  // Sur la page Administration, le bandeau troque la recherche contre un
  // en-tête « ← Administration ». L'avatar reste à droite pour garder le même
  // bandeau.
  if (view === 'settings') {
    return (
      <div className={styles.header}>
        <div className={styles.titleWrap}>
          <Button
            appearance="subtle"
            size="small"
            icon={<ArrowLeft20Regular />}
            onClick={() => setView('tree')}
            title="Retour"
            aria-label="Retour"
          />
          <Text className={styles.title}>Administration</Text>
        </div>
        <UserMenu />
      </div>
    );
  }

  return (
    <div className={styles.header}>
      <div className={styles.searchWrap}>
        <Input
          className={styles.search}
          placeholder="Rechercher…"
          value={searchQuery}
          contentBefore={<Search20Regular />}
          onChange={(_e, data) => {
            setSearchQuery(data.value);
            addBreadcrumb({ category: 'ui.click', message: 'search-query' });
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && searchQuery) {
              e.preventDefault();
              setSearchQuery('');
            }
          }}
          contentAfter={
            <span className={styles.after}>
              {searchQuery && (
                <Button
                  appearance="transparent"
                  size="small"
                  className={styles.iconBtn}
                  icon={<Dismiss20Regular />}
                  aria-label="Effacer la recherche"
                  onClick={() => setSearchQuery('')}
                />
              )}
              {/* Le sélecteur de PORTÉE seul a laissé place au panneau complet :
                  portée + critères (De, À, Objet, dates, pièces jointes, auteur du
                  rangement). Voir SearchFiltersPopover pour le raisonnement. */}
              <SearchFiltersPopover
                effectiveScope={effectiveScope}
                canContainer={canContainer}
                canFolder={canFolder}
              />
            </span>
          }
        />
      </div>
      <UserMenu />
    </div>
  );
}
